import {
  GeckoHorizontalWeld,
  GeckoHorizontalWeldJSON,
  GeckoVerticalWeld,
  GeckoVerticalWeldJSON,
} from 'types'
import {
  GeckoComponentModelType,
  GeckoFeatureModelType,
} from 'types/assetModel/assetModelEnums'
import { extractFeatureMap } from 'types/assetModel/assetModelHelpers'
import {
  compassOrientationFormField,
  installationDateFormField,
  tminFormField,
  wallThicknessFormField,
} from 'types/assetModel/commonFields'
import {
  GeckoEditableComponent,
  SceneConfig,
} from 'types/assetModel/components/geckoEditableComponent'
import {
  GeckoPlate,
  GeckoPlateJSON,
} from 'types/assetModel/features/geckoPlate'
import { weightThicknessValue } from 'types/assetModel/thicknessUnitHelpers'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  getNumberFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import { assetModelPrecision } from 'utils/constants'
import { forecastMath } from 'utils/fcMath'
import { parseDate, parseNumber, parseString } from 'utils/parsingHelpers'

const { pi } = forecastMath
import {
  optionalStringSchema,
  requiredNumberSchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

type HoodBendSupportedFeatures =
  | GeckoPlateJSON
  | GeckoVerticalWeldJSON
  | GeckoHorizontalWeldJSON

export type HoodBendAssetModelJSON = {
  name: string
  type: GeckoComponentModelType.HoodBend
  major_radius: number
  minor_radius: number
  torus_angle: number
  material: string | undefined
  installation_date: string | undefined
  features: Record<string, HoodBendSupportedFeatures>
  scene_config: SceneConfig
  component_bounds: Record<string, number>
  nominal: {
    wall_thickness?: number
  }
  tmin: number | undefined
  compass_orientation?: number
}

export const HoodBendAvailableFeatures: GeckoFeatureModelType[] = [
  GeckoFeatureModelType.Plate,
  GeckoFeatureModelType.VerticalWeld,
  GeckoFeatureModelType.HorizontalWeld,
]

export const NewHoodBendAssetModelJSON = {
  name: 'New Hood Bend',
  type: GeckoComponentModelType.HoodBend,
  major_radius: 0,
  minor_radius: 0,
  torus_angle: 90, // Default to 90 degrees
  material: undefined,
  installation_date: undefined,
  joint_efficiency: undefined,
  stress_tolerance: undefined,
  features: {},
  scene_config: {},
  component_bounds: {
    x_start: 0,
    y_start: 0,
    x_end: 0,
    y_end: 0,
  },
  tmin: undefined,
  compass_orientation: undefined,
  nominal: {},
} as HoodBendAssetModelJSON

export class GeckoHoodBend extends GeckoEditableComponent {
  type: GeckoComponentModelType.HoodBend
  majorRadius: GeckoMeasurement
  minorRadius: GeckoMeasurement
  torusAngle: number // in degrees
  material: string | undefined
  installationDate: Date | undefined
  componentBounds: GeckoComponentRect
  sceneConfig: SceneConfig
  plates: Map<string, GeckoPlate>
  verticalWelds: Map<string, GeckoVerticalWeld>
  horizontalWelds: Map<string, GeckoHorizontalWeld>
  wallNominalThickness: GeckoMeasurement | undefined
  tmin: GeckoMeasurement | undefined
  compassOrientation: number | undefined

  private constructor(
    id: string,
    json: Record<string, unknown>,
    name: string,
    majorRadius: GeckoMeasurement,
    minorRadius: GeckoMeasurement,
    torusAngle: number,
    material: string | undefined,
    installationDate: Date | undefined,
    componentBounds: GeckoComponentRect,
    sceneConfig: SceneConfig,
    plates: Map<string, GeckoPlate>,
    verticalWelds: Map<string, GeckoVerticalWeld>,
    horizontalWelds: Map<string, GeckoHorizontalWeld>,
    wallNominalThickness: GeckoMeasurement | undefined,
    tmin: GeckoMeasurement | undefined,
    compassOrientation: number | undefined
  ) {
    super(id, name, json)
    this.type = GeckoComponentModelType.HoodBend
    this.majorRadius = majorRadius
    this.minorRadius = minorRadius
    this.torusAngle = torusAngle
    this.material = material
    this.installationDate = installationDate
    this.componentBounds = componentBounds
    this.sceneConfig = sceneConfig
    this.plates = plates
    this.verticalWelds = verticalWelds
    this.horizontalWelds = horizontalWelds
    this.wallNominalThickness = wallNominalThickness
    this.tmin = tmin
    this.compassOrientation = compassOrientation
  }

  public get platesArray() {
    return Array.from(this.plates.values())
  }

  public get verticalWeldsArray() {
    return Array.from(this.verticalWelds.values())
  }

  public get horizontalWeldsArray() {
    return Array.from(this.horizontalWelds.values())
  }

  public static CreateFromJSON(id: string, json: HoodBendAssetModelJSON) {
    const name = json.name as string
    const compassOrientation = json.compass_orientation
    const hoodMajorRadius = GeckoMeasurement.CreateFromValue(
      json.major_radius as number,
      GeckoMeasurementUnit.Meters
    )
    const hoodMinorRadius = GeckoMeasurement.CreateFromValue(
      json.minor_radius as number,
      GeckoMeasurementUnit.Meters
    )
    const torusAngle = json.torus_angle as number
    const material = parseString(json.material)
    const installationDate = parseDate(json.installation_date)

    const componentBoundsData = json.component_bounds as Record<string, number>
    const componentBounds = GeckoComponentRect.CreateFromXYs(
      id,
      componentBoundsData.x_start,
      componentBoundsData.y_start,
      componentBoundsData.x_end,
      componentBoundsData.y_end,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const sceneConfig = json.scene_config as SceneConfig

    const featureData = json.features as Record<
      string,
      HoodBendSupportedFeatures
    >

    const plates = extractFeatureMap(
      featureData,
      GeckoFeatureModelType.Plate,
      (filtered) =>
        GeckoPlate.CreateMapFromJSON(
          id,
          filtered as Record<string, GeckoPlateJSON>
        )
    )

    const horizontalWelds = extractFeatureMap(
      featureData,
      GeckoFeatureModelType.HorizontalWeld,
      (filtered) =>
        GeckoHorizontalWeld.CreateMapFromJSON(
          id,
          filtered as Record<string, GeckoHorizontalWeldJSON>
        )
    )

    const verticalWelds = extractFeatureMap(
      featureData,
      GeckoFeatureModelType.VerticalWeld,
      (filtered) =>
        GeckoVerticalWeld.CreateMapFromJSON(
          id,
          filtered as Record<string, GeckoVerticalWeldJSON>
        )
    )

    const nominalJSON = json.nominal
    const parsedWallNominalThickness = parseNumber(nominalJSON.wall_thickness)
    const wallNominalThickness =
      parsedWallNominalThickness !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedWallNominalThickness,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined
    const tminJson = json.tmin
    const parsedTmin = parseNumber(tminJson)
    const tmin =
      parsedTmin !== undefined
        ? weightThicknessValue(
            GeckoMeasurement.CreateFromValue(
              parsedTmin,
              GeckoMeasurementUnit.Meters
            )
          )
        : undefined

    return new GeckoHoodBend(
      id,
      json,
      name,
      hoodMajorRadius,
      hoodMinorRadius,
      torusAngle,
      material,
      installationDate,
      componentBounds,
      sceneConfig,
      plates,
      verticalWelds,
      horizontalWelds,
      wallNominalThickness,
      tmin,
      compassOrientation
    )
  }

  public asJSON(): HoodBendAssetModelJSON {
    return generateHoodBend(
      this.name,
      this.majorRadius,
      this.minorRadius,
      this.torusAngle,
      this.material,
      this.installationDate,
      this.wallNominalThickness,
      this.tmin,
      this.compassOrientation,
      this.json as HoodBendAssetModelJSON
    )
  }
}

const generateHoodBend = (
  name: string,
  majorRadius: GeckoMeasurement,
  minorRadius: GeckoMeasurement,
  torusAngle: number,
  material: string | undefined,
  installationDate: Date | undefined,
  wallNominalThickness: GeckoMeasurement | undefined,
  tmin: GeckoMeasurement | undefined,
  compassOrientation: number | undefined,
  otherHoodBendData: HoodBendAssetModelJSON = NewHoodBendAssetModelJSON
): HoodBendAssetModelJSON => {
  const hoodBendMajorRadius = majorRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  const hoodBendMinorRadius = minorRadius
    .convertTo(GeckoMeasurementUnit.Meters)
    .toPrecision(assetModelPrecision).num

  // Calculate approximate bounds for the torus bend
  // The x extent is roughly the arc length of the major radius
  const arcLength = (torusAngle * pi * hoodBendMajorRadius) / 180
  // The y extent is the diameter of the minor radius
  const minorDiameter = hoodBendMinorRadius * 2

  const wallNominalThicknessMeters =
    wallNominalThickness !== undefined
      ? wallNominalThickness
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const tminMeters =
    tmin !== undefined
      ? tmin
          .convertTo(GeckoMeasurementUnit.Meters)
          .toPrecision(assetModelPrecision).num
      : undefined

  const parsedInstallationDate = installationDate
    ? installationDate.toISOString().split('T')[0]
    : undefined

  return {
    name: name,
    type: GeckoComponentModelType.HoodBend,
    major_radius: hoodBendMajorRadius,
    minor_radius: hoodBendMinorRadius,
    torus_angle: torusAngle,
    material: material,
    installation_date: parsedInstallationDate,
    features: otherHoodBendData.features,
    scene_config: otherHoodBendData.scene_config,
    component_bounds: {
      x_start: 0,
      y_start: 0,
      x_end: arcLength,
      y_end: minorDiameter,
    },
    tmin: tminMeters,
    compass_orientation: compassOrientation,
    nominal: { wall_thickness: wallNominalThicknessMeters },
  }
}

export const HoodBendAssetModelFormFields = (
  hoodBendJSON: HoodBendAssetModelJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Hood Bend Name',
      value: hoodBendJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'major_radius',
      label: 'Major Radius',
      value: hoodBendJSON.major_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'minor_radius',
      label: 'Minor Radius',
      value: hoodBendJSON.minor_radius,
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'torus_angle',
      label: 'Torus Angle',
      value: hoodBendJSON.torus_angle,
      unit_magnitude: null,
      validationRule: requiredNumberSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Number,
    },
    {
      dataKey: 'material',
      label: 'Material',
      value: hoodBendJSON.material,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    installationDateFormField(hoodBendJSON.installation_date, true),
    wallThicknessFormField(hoodBendJSON.nominal.wall_thickness),
    tminFormField(hoodBendJSON.tmin),
    compassOrientationFormField(hoodBendJSON.compass_orientation),
  ]
}

export const HoodBendJSONFromFormFields = (
  component: HoodBendAssetModelJSON,
  formFields: FormField[]
) => {
  const parsedWallNominalThickness = parseNumber(
    getFormFieldByDataKey(formFields, 'wall_thickness')?.value
  )
  const parsedTmin = parseNumber(
    getFormFieldByDataKey(formFields, 'tmin')?.value
  )
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )
  const compassOrientation = getNumberFieldByDataKey(
    formFields,
    'compass_orientation'
  )
  return generateHoodBend(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'major_radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    GeckoMeasurement.CreateFromValue(
      getFormFieldByDataKey(formFields, 'minor_radius')?.value as number,
      GeckoMeasurementUnit.Meters
    ),
    getFormFieldByDataKey(formFields, 'torus_angle')?.value as number,
    parseString(getFormFieldByDataKey(formFields, 'material')?.value),
    installationDate?.value
      ? new Date(installationDate?.value as string)
      : undefined,
    parsedWallNominalThickness !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedWallNominalThickness,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    parsedTmin !== undefined
      ? GeckoMeasurement.CreateFromValue(
          parsedTmin,
          GeckoMeasurementUnit.Meters
        )
      : undefined,
    compassOrientation,
    component
  )
}
